import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";

import Layout from "./components/Layout";
import LanguageUrlSync from "./components/LanguageUrlSync";
import Home from "./pages/Home";
import Donate from "./pages/Donate";
import DonationSuccess from "./pages/DonationSuccess";
import About from "./pages/About";
import Projects from "./pages/Projects";
import Involved from "./pages/Involved";
import Contact from "./pages/Contact";
import International from "./pages/International";
import CountryProgram from "./pages/CountryProgram";
import Updates from "./pages/Updates";
import EventPage from "./pages/EventPage";
import EventsImpact from "./pages/EventsImpact";
import SingleEventPage from "./pages/SingleEventPage";
import Volunteer from "./pages/Volunteer";
import ImpactPage from "./pages/ImpactPage";
import DistributionsPage from "./pages/DistributionsPage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CheckInPage from "./pages/CheckInPage";
import GalleryUploadPage from "./pages/GalleryUploadPage";
import GalleryLivePage from "./pages/GalleryLivePage";
import Gallery from "./pages/Gallery";
import Campaigns from "./pages/Campaigns";
import CampaignDetail from "./pages/CampaignDetail";

// Admin pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminHelp from "./pages/admin/AdminHelp";
import TrainingDashboard from "./pages/admin/TrainingDashboard";
import AdminFinance from "./pages/admin/AdminFinance";
import PopupLiveKiosk from "./pages/admin/PopupLiveKiosk";
import TeamLogin from "./pages/admin/TeamLogin";
import TeamPortal from "./pages/admin/TeamPortal";
import AuthCallback from "./pages/admin/AuthCallback";
import ForgotPassword from "./pages/admin/ForgotPassword";
import ResetPassword from "./pages/admin/ResetPassword";
import PopupShop from "./pages/PopupShop";
import EventPopupShop from "./pages/EventPopupShop";
import PublicOutreachQuickLog from "./pages/PublicOutreachQuickLog";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Gallery upload pages (no Layout, mobile-friendly)
  if (location.pathname.startsWith('/gallery/')) {
    return (
      <Routes>
        <Route path="/gallery/live" element={<GalleryLivePage />} />
        <Route path="/gallery/:galleryId/upload" element={<GalleryUploadPage />} />
      </Routes>
    );
  }

  // Pop-Up Shop pages (no Layout, mobile-first)
  if (location.pathname.startsWith('/popup/') || location.pathname === '/popup-shop') {
    return (
      <Routes>
        <Route path="/popup-shop" element={<EventPopupShop />} />
        <Route path="/popup/:slug" element={<PopupShop />} />
      </Routes>
    );
  }

  // Outreach Quick-Log public volunteer page (no Layout, mobile-first, token-authed)
  if (location.pathname.startsWith('/outreach/quick-log/')) {
    return (
      <Routes>
        <Route path="/outreach/quick-log/:token" element={<PublicOutreachQuickLog />} />
      </Routes>
    );
  }

  // Admin routes without Layout
  if (location.pathname.startsWith('/admin')) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot-password" element={<ForgotPassword />} />
        <Route path="/admin/reset-password" element={<ResetPassword />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/help" element={<AdminHelp />} />
        <Route path="/admin/training" element={<TrainingDashboard />} />
        <Route path="/admin/finance" element={<AdminFinance />} />
        <Route path="/admin/popup-live" element={<PopupLiveKiosk />} />
        <Route path="/admin/team/login" element={<TeamLogin />} />
        <Route path="/admin/team/portal" element={<TeamPortal />} />
        <Route path="/admin/auth/callback" element={<AuthCallback />} />
        <Route path="/admin" element={<AdminLogin />} />
      </Routes>
    );
  }

  // Public routes with Layout
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/donate/success" element={<DonationSuccess />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/campaigns/:slug" element={<CampaignDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/involved" element={<Involved />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/international" element={<International />} />
        <Route path="/international/:programId" element={<CountryProgram />} />
        <Route path="/updates" element={<Updates />} />
        <Route path="/events" element={<EventsImpact />} />
        <Route path="/events/:eventId" element={<SingleEventPage />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/impact" element={<ImpactPage />} />
        <Route path="/distributions" element={<DistributionsPage />} />
        <Route path="/distributions/:id" element={<DistributionsPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/:itemId" element={<ProductPage />} />
        <Route path="/checkin/:entryId" element={<CheckInPage />} />
        <Route path="/event" element={<EventPage />} />
        <Route path="/volunteer" element={<Volunteer />} />
        <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <ScrollToTop />
            <LanguageUrlSync />
            <AppRouter />
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
